// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Share } from "@capacitor/share";
import { PageHeaderWrapper, Tab, Tabs } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useSetSearchParams } from "@src/appV2/lib/utils/useSetSearchParams";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";

import { useEmbeddedMode } from "../EmbeddedMode/EmbeddedModeContext";
import { useDefinedWorker } from "../Worker/useDefinedWorker";
import { useCreateShareLink } from "./api/useCreateShareLink";
import { useGetReferrals } from "./api/useGetReferrals";
import { useReferralsEligibilityV2 } from "./api/useReferralsEligibilityV2";
import { useMaxReferralBonusAmount } from "./Bonus/flags";
import { useFlexActivationMessaging } from "./hooks/useFlexActivationMessaging";
import { MyReferralsPageContent } from "./MyReferrals/PageContent";
import { REFERRAL_TABS } from "./path";
import { getEmbeddedReferralLink } from "./RedeemCode/utils";
import { ReferralShareLinkPageContent } from "./ShareLink/PageContent";

type ReferralTab = (typeof REFERRAL_TABS)[keyof typeof REFERRAL_TABS];

export function WorkerReferralsPage() {
  const { tab: selectedTab = REFERRAL_TABS.SHARE } = useSearchParams<{ tab: ReferralTab }>();
  const setSearchParams = useSetSearchParams();

  const worker = useDefinedWorker();

  const { mutateAsync: createShareLink, isLoading } = useCreateShareLink();
  const { showSuccessToast, showErrorToast } = useToast();

  const {
    data: referrals,
    isLoading: isReferralsLoading,
    isError: isReferralsError,
  } = useGetReferrals({});

  const {
    data: referralsEligibility,
    isLoading: isReferralsEligibilityLoading,
    isError: isReferralsEligibilityError,
  } = useReferralsEligibilityV2(
    {
      workerId: worker?.userId,
    },
    {
      enabled: true,
    }
  );

  const maxReferralBonusAmount = useMaxReferralBonusAmount("postOnboardingBonusInDollars");
  const { getReferralRequirementMessage } = useFlexActivationMessaging();

  const { isEmbedded } = useEmbeddedMode();

  const handleShareReferralLink = async () => {
    if (isEmbedded) {
      await navigator.clipboard.writeText(getEmbeddedReferralLink(worker.referralCode));
      showSuccessToast("Link copied to clipboard");
      return;
    }

    const { data: referralLinkDataArray } = await createShareLink();

    // eslint-disable-next-line prefer-destructuring
    const referralLinkData = referralLinkDataArray[0];

    if (!isDefined(referralLinkData)) {
      showErrorToast("Failed to create referral link");
      return;
    }

    if (isCapacitorPlatform()) {
      await Share.share({
        title: "Join Clipboard",
        text: `Hey! Use my link to join Clipboard and we both get a $${maxReferralBonusAmount} bonus when you ${getReferralRequirementMessage()}! There are lots of opportunities waiting for you on Clipboard.\n\n${
          referralLinkData.attributes.referralLink
        }`,
      });
    } else {
      await navigator.clipboard.writeText(referralLinkData.attributes.referralLink);
      showSuccessToast("Link copied to clipboard");
    }

    logEvent(APP_V2_USER_EVENTS.REFERRAL_SHARE_LINK_CLICKED, {
      referralLink: referralLinkData.attributes.referralLink,
      bonusAmountAdvertised: maxReferralBonusAmount,
      source: "referral_share_link_page",
    });
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          backgroundColor: theme.palette.common.white,
          ...getSafeAreaPaddings({
            bottom: true,
            fallbackBottom: theme.spacing(4),
          }),
        })}
      >
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow sx={{ pb: 0 }} />
          <Stack alignItems="center">
            <Tabs
              value={selectedTab}
              variant="underlined"
              onChange={(_, value: ReferralTab) => {
                setSearchParams({ tab: value }, { shouldReplace: true });
              }}
            >
              <Tab variant="underlined" label="Share" value={REFERRAL_TABS.SHARE} />
              <Tab variant="underlined" label="My Referrals" value={REFERRAL_TABS.MY_REFERRALS} />
            </Tabs>
          </Stack>
        </PageHeaderWrapper>
        {selectedTab === REFERRAL_TABS.SHARE && (
          <ReferralShareLinkPageContent
            isLoading={isLoading}
            maxReferralBonusAmount={maxReferralBonusAmount}
            onShareReferral={handleShareReferralLink}
          />
        )}
        {selectedTab === REFERRAL_TABS.MY_REFERRALS && (
          <MyReferralsPageContent
            timezone={worker?.tmz ?? new Intl.DateTimeFormat().resolvedOptions().timeZone}
            isError={isReferralsError || isReferralsEligibilityError}
            isLoading={isLoading || isReferralsLoading || isReferralsEligibilityLoading}
            appliedReferrals={referrals ?? []}
            referralsEligibility={referralsEligibility?.data ?? []}
            outgoingReferralDetails={{
              bonusAmount: maxReferralBonusAmount,
              workRequirementMessage: getReferralRequirementMessage(),
              workRequirementMessageThirdPerson: getReferralRequirementMessage({
                useThirdPerson: true,
              }),
            }}
          />
        )}
      </PageWrapper>
    </ThemeProvider>
  );
}
